import { forwardRef, type InputHTMLAttributes, type ReactElement, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { Field as BaseField } from '@base-ui/react/field'
import { Form as BaseForm } from '@base-ui/react/form'
import { CircleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

export type FieldMessageProps = {
  /** Visible label. Every field has one. */
  label: ReactNode
  /** Help that people need to complete the field. Optional explanations belong in an info tip. */
  description?: ReactNode
  /** Adds “(optional)” to the label. Mark optional fields, not required ones. */
  optional?: boolean
  /** An error from outside the field, such as the server. Shown while it is set. */
  error?: ReactNode
  /** Shown when a required field is empty, in place of the browser’s message. */
  requiredMessage?: string
  /** Custom validation, run after the native checks. Return a message when invalid. */
  validate?: BaseField.Root.Props['validate']
  /** When to validate. Defaults to on submit, then on change once a field has an error. */
  validationMode?: BaseField.Root.Props['validationMode']
}

export type FieldLabelProps = {
  /** Hides the label visually. It still names the field for assistive technology. Only for fields whose purpose the
   *  surrounding layout already makes obvious, such as a search box with a search icon or a chat composer. */
  hideLabel?: boolean
}

export type LabelAccessoryProps = {
  /** Content beside the label on the same row, such as an info tip. It sits outside the label element, so it never
   *  joins the control's accessible name. An icon button in it takes the compact look and keeps its 44px target. */
  labelAccessory?: ReactNode
}

/** Field sizes. Compact looks 36px, like a compact Button, for dense toolbars and tables; both keep a 44px target. */
export type FieldSize = 'default' | 'compact'

type FieldControlStyle = {
  /** Compact: an input looks 36px and keeps its 44px target; a textarea tightens its padding and lets `rows` set its
   *  height down to 44px. For dense toolbars and tables; forms stay default size. */
  size?: FieldSize
  /** System monospace with tabular figures, for URLs, keys and codes people copy or compare. Pair with `readOnly`. */
  mono?: boolean
}

const controlClassName = ({ size = 'default', mono = false }: FieldControlStyle, ...extra: (string | false | undefined)[]) =>
  cn('play-field', ...extra, size === 'compact' && 'play-field--compact', mono && 'play-field--mono')

export type ShellProps = FieldMessageProps & FieldLabelProps & LabelAccessoryProps & { name?: string; disabled?: boolean; className?: string; children: ReactNode }

/**
 * Puts an accessory beside a label, outside the label element. Without one the label renders alone, as before.
 * `block` is the BEM block that styles the row: `play-form-field` for fields and selects, `play-choice` for choices.
 */
export function LabelRow({ accessory, block, children }: { accessory?: ReactNode; block: 'play-form-field' | 'play-choice'; children: ReactElement }) {
  if (accessory == null || accessory === false) return children
  return (
    <div className={`${block}__label-row`}>
      {children}
      <span className={`${block}__accessory play-label-accessory`}>{accessory}</span>
    </div>
  )
}

/** Description and error for a field. Errors carry a glyph as well as color and stay until the value is fixed. */
export function FieldMessages({ description, error, requiredMessage }: Pick<FieldMessageProps, 'description' | 'error' | 'requiredMessage'>) {
  return (
    <>
      {description && <BaseField.Description className="play-form-field__description">{description}</BaseField.Description>}
      <BaseField.Validity>
        {(state) => {
          const override = error ?? (state.validity.valueMissing && requiredMessage ? requiredMessage : undefined)
          // Without an override, Base UI supplies the message as `children`: a server error from the Form by field
          // name, or the validity error. Passing children of our own would replace it.
          return (
            <BaseField.Error
              className="play-form-field__error"
              match={error ? true : undefined}
              render={({ children, ...props }) => (
                <div {...props}>
                  <CircleAlert aria-hidden="true" />
                  <span>{override ?? children}</span>
                </div>
              )}
            />
          )
        }}
      </BaseField.Validity>
    </>
  )
}

export function FieldShell({ label, hideLabel, labelAccessory, description, optional, error, requiredMessage, validate, validationMode, name, disabled, className, children }: ShellProps) {
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      validate={validate}
      validationMode={validationMode}
      className={cn('play-form-field', className)}
    >
      <LabelRow block="play-form-field" accessory={labelAccessory}>
        <BaseField.Label className={cn('play-form-field__label', hideLabel && 'play-form-field__label--hidden')}>
          {label}
          {optional && <span className="play-form-field__optional"> (optional)</span>}
        </BaseField.Label>
      </LabelRow>
      {children}
      <FieldMessages description={description} error={error} requiredMessage={requiredMessage} />
    </BaseField.Root>
  )
}

export type TextFieldProps = FieldMessageProps & FieldLabelProps & LabelAccessoryProps & FieldControlStyle & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'children'> & {
  /** A Lucide icon at the start of the input, such as Search. Decorative: the label still names the field. */
  icon?: ReactElement
}

/** A labelled single-line input with its help and validation. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hideLabel, labelAccessory, icon, size, mono, description, optional, error, requiredMessage, validate, validationMode, name, disabled, className, ...input },
  ref,
) {
  const control = <BaseField.Control ref={ref} className={controlClassName({ size, mono }, icon && 'play-field--with-icon')} {...input} />
  return (
    <FieldShell {...{ label, hideLabel, labelAccessory, description, optional, error, requiredMessage, validate, validationMode, name, disabled, className }}>
      {icon ? (
        <span className={cn('play-field-adornment', size === 'compact' && 'play-field-adornment--compact')}>
          {control}
          <span className="play-field__icon" aria-hidden="true">{icon}</span>
        </span>
      ) : control}
    </FieldShell>
  )
})

export type TextAreaFieldProps = FieldMessageProps & FieldLabelProps & LabelAccessoryProps & FieldControlStyle & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'>

/** A labelled multi-line input. It grows vertically when people drag it. */
export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField(
  { label, hideLabel, labelAccessory, size, mono, description, optional, error, requiredMessage, validate, validationMode, name, disabled, className, rows = 4, id, ...textarea },
  ref,
) {
  return (
    <FieldShell {...{ label, hideLabel, labelAccessory, description, optional, error, requiredMessage, validate, validationMode, name, disabled, className }}>
      {/* The id goes to the Control so the field's label and messages point at it. */}
      <BaseField.Control id={id} render={<textarea ref={ref} rows={rows} className={controlClassName({ size, mono })} {...textarea} />} />
    </FieldShell>
  )
})

export type FormProps = Omit<BaseForm.Props, 'className'> & { className?: string }

/** A form that validates its Play fields together and maps server errors to fields by name. */
export function Form({ className, ...props }: FormProps) {
  return <BaseForm className={cn('play-form', className)} {...props} />
}

